import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

const DEFAULT_MESSAGE = 'You have unsaved changes. If you leave this page, they will be lost.';

/**
 * Warns before unsaved edits are lost. A reload or tab close gets the browser's own prompt
 * (`beforeunload`); in-app navigation to another page is blocked and `blocked` turns true,
 * so the page (through `RecordPageShell`'s `guard`) asks with a `ConfirmModal`.
 * Same-page URL changes (tab, `?version=`) never block. Needs the data router (`index.tsx`).
 *
 * After a successful save or create, call `release()` before navigating: the draft only
 * reads clean on the next render, which is too late for the redirect.
 */
export function useUnsavedGuard(dirty: boolean, message: string = DEFAULT_MESSAGE) {
  const released = useRef(false);
  useEffect(() => {
    released.current = false;
  }, [dirty]);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        dirty && !released.current && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return {
    blocked: blocker.state === 'blocked',
    message,
    /** Keep editing. */
    stay: () => blocker.reset?.(),
    /** Discard the edits and go where the operator was headed. */
    leave: () => blocker.proceed?.(),
    release: () => {
      released.current = true;
    },
  };
}

export type UnsavedGuard = ReturnType<typeof useUnsavedGuard>;
